import { Fragment, useCallback, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import { useSelector } from 'react-redux';
import { Divider } from '@ui-kitten/components';
import lodashGet from 'lodash/get';
import lodashIsEmpty from 'lodash/isEmpty';

import ReservationCancellationPolicy from '../../Components/ReservationCancellationPolicy';
import ListItem from '../../Components/ListItem';
import ReadMore from '../../Components/ReadMore';
import { textCategory } from '../../Components/Reservation/constants';
import PageNoResult from '../../Components/Reservation/NoResult';
import {
  ids as reservationStyleIds,
  styles as reservationStyles,
} from '../../Components/Reservation/styles';
import ResponsiveText from '../../Components/ResponsiveText';
import ScreenBanner from '../../Components/ScreenBanner';
import ResponsiveLayout from '../../Components/ScreenWrapper/ResponsiveLayout';
import {
  ItemInfo,
  ReservationType as ResType,
} from '../../Components/Sheets/BookReservation/types';
import constants from '../../Config/constants';
import { getReservationStoreParams } from '../../Helper';
import DateHelper from '../../Helper/Date';
import useGoBack from '../../Hooks/useGoBack';
import usePageView from '../../Hooks/usePageView';
import useReservationBooking from '../../Hooks/useReservationBooking';
import useToast from '../../Hooks/useToast';
import routeList from '../../Routes/list';
import { useGetEventDetailsQuery } from '../../RTK/api/events';
import { selectReservationStoreFilter } from '../../RTK/filter/selectors';
import themeStyle from '../../Theme/styles';
import {
  ids as platformStyleIds,
  styles as platformStyles,
} from '../../Theme/styles/platform';

import MapsAndInfo from '../ReservationStoreDetails/components/MapAndInfo';

import CheckAvailability from './components/CheckAvailability';
import Overview from './components/Overview';
import OverviewAndPartySize from './components/OverviewAndPartySize';
import StoreAndTags from './components/StoreAndTags';
import PageLoader from './PageLoader';

const ReservationEventDetails = ({ navigation, route }) => {
  const eventId = lodashGet(route, 'params.eventId');
  const size = Number(lodashGet(route, 'params.size') || 1);
  const selectedDate = lodashGet(route, 'params.selectedDate');
  const isPreview = route?.name === routeList.RES_EVENT_DETAILS_PREVIEW;

  const soldOutSize = useRef<number | undefined>();
  const {
    availabilityRef,
    showBookingSchedulePicker,
    selectBookingSchedule,
    navigateToCheckoutDestination,
  } = useReservationBooking();
  const toast = useToast();
  const filter = useSelector(selectReservationStoreFilter);

  const [justSoldOut, setJustSoldOut] = useState(false);
  const [justEnded, setJustEnded] = useState(false);

  const { data: eventInfo, isLoading } = useGetEventDetailsQuery(
    { payload: eventId },
    {
      skip: !eventId,
      refetchOnMountOrArgChange: true, // so when reopen the event page on reservation it refresh the details
    },
  );
  const goBack = useGoBack({
    backRoute: routeList.RES_STORE_DETAILS,
    backRouteParams: getReservationStoreParams(
      { filter: { ...filter, storeId: eventInfo?.store?.id } },
      constants.RESERVATION_TYPE.EVENT,
    ),
    isDefaultGoBack: !constants.isWeb,
  });

  const [longitude, latitude] = eventInfo?.location?.split?.(',') || [];

  const eventStatus = useMemo(
    () =>
      DateHelper.getEventScheduleInfo(
        eventInfo?.start_date,
        eventInfo?.end_date,
        eventInfo?.time_slots,
        eventInfo?.available_days,
      ),
    [
      eventInfo?.available_days,
      eventInfo?.start_date,
      eventInfo?.end_date,
      eventInfo?.time_slots,
    ],
  );
  const isMultiple =
    eventStatus.remainingDay >= 1 || eventInfo?.tickets?.length > 1;

  const _onSuccess = useCallback(
    (data) => navigateToCheckoutDestination(navigation, data),
    [navigation, navigateToCheckoutDestination],
  );

  const _onGetTickets = useCallback(
    async (s: number) => {
      const schedInfoRes = DateHelper.getEventScheduleInfo(
        eventInfo?.start_date,
        eventInfo?.end_date,
        eventInfo?.time_slots,
        eventInfo?.available_days,
      );
      const eventDate = eventStatus.start;
      const itemInfoWithType = {
        ...eventInfo,
        type: ResType.EVENT,
      } as ItemInfo;
      if (schedInfoRes.isFinalDayOngoing) {
        toast.show('Sorry, The event is no longer available. :(', false);
        setJustEnded(true);
      } else if (isMultiple) {
        const schedPickerRes = await showBookingSchedulePicker({
          filter: { size: s, date: selectedDate },
          itemInfo: itemInfoWithType, // force eventInfo to ItemInfo its basically EventDetails but extended for mix type of EventDetails and TableDetails
          storeInfo: eventInfo.store,
        });
        if (schedPickerRes?.success) {
          _onSuccess(schedPickerRes.data);
        }
      } else {
        // if event is single date and single time slot and no previous payment url
        setJustSoldOut(false);
        selectBookingSchedule({
          itemInfo: {
            ...itemInfoWithType,
            // @ts-ignore: this is sure to be event type
            eventSelectedTs: eventInfo.time_slots[0],
          }, // force eventInfo to ItemInfo its basically EventDetails but extended for mix type of EventDetails and TableDetails
          dateAndTime: {
            date: eventDate.format(constants.DBDATE_FORMAT),
            time: eventDate.format(constants.DBTIME_FORMAT),
          },
          size: s,
          storeInfo: eventInfo.store,
          onError: () => {
            soldOutSize.current = size;
            setJustSoldOut(true);
          },
          onSuccess: _onSuccess,
        });
      }
    },
    [
      eventInfo,
      eventStatus.start,
      isMultiple,
      selectedDate,
      size,
      toast,
      _onSuccess,
      selectBookingSchedule,
      showBookingSchedulePicker,
    ],
  );

  const storeAndTagsProps = useMemo(
    () => ({
      name: eventInfo?.store?.name,
      location_address: eventInfo?.location_address,
      tags: eventInfo?.store?.store_tags,
      onGoBack: goBack,
    }),
    [
      eventInfo?.location_address,
      eventInfo?.store?.name,
      eventInfo?.store?.store_tags,
      goBack,
    ],
  );

  const overviewAndPartySizeProps = useMemo(
    () => ({
      eventId,
      eventItemId: eventInfo?.tickets?.[0]?.item?.id,
      isEnded: eventStatus.isFinalDayOngoing || justEnded,
      isMultiple,
      isSoldOut: justSoldOut,
      size,
      soldOutQty: soldOutSize.current,
      startDate: eventInfo?.start_date,
      onPress: _onGetTickets,
    }),
    [
      eventId,
      eventInfo?.start_date,
      eventInfo?.tickets,
      eventStatus.isFinalDayOngoing,
      isMultiple,
      justEnded,
      justSoldOut,
      size,
      _onGetTickets,
    ],
  );

  usePageView({
    enabled: !isLoading,
    page_event_id: eventInfo?.id,
    screen_class: 'ReservationEventDetails',
    screenName: `Reservation | Event | ${eventInfo?.name}`,
  });

  if (isLoading) {
    return <PageLoader />;
  } else if (lodashIsEmpty(eventInfo)) {
    return <PageNoResult />;
  }

  return (
    <Fragment>
      <ResponsiveLayout
        title={constants.isWeb ? '' : eventInfo.name}
        LeftComponent={
          <View style={themeStyle.flex1}>
            {/* Top spacer for smaller screen */}
            {isPreview && (
              <View
                dataSet={{ media: platformStyleIds.mobileAndTabletOnly }}
                style={[
                  platformStyles.mobileAndTabletOnly,
                  themeStyle.spacingTop,
                ]}
              />
            )}
            {/* Web Store Name And Tags */}
            <View
              dataSet={{ media: platformStyleIds.webOnly }}
              style={[platformStyles.webOnly, themeStyle.spacingBottom]}
            >
              <StoreAndTags {...storeAndTagsProps} />
            </View>
            {/* @ts-ignore: store_logo is not required, the file is just a js type and cannot add type checking on the props */}
            <ScreenBanner banner={eventInfo.image} />
            <View style={reservationStyles.contentContainer}>
              <ResponsiveText
                category={textCategory.LARGE_HEADING}
                style={[themeStyle.spacingBottomMedium, themeStyle.bold]}
              >
                {eventInfo.name}
              </ResponsiveText>
              {/* Mobile Store Name And Tags */}
              <StoreAndTags
                dataSet={{ media: platformStyleIds.mobileAndTabletOnly }}
                style={platformStyles.mobileAndTabletOnly}
                {...storeAndTagsProps}
              />
              <View
                dataSet={{ media: platformStyleIds.mobileAndTabletOnly }}
                style={platformStyles.mobileAndTabletOnly}
              >
                <View style={themeStyle.spacingTopMedium}>
                  <Overview eventId={eventId} />
                </View>
                <Divider
                  dataSet={{ media: reservationStyleIds.sectionDivider }}
                  style={[
                    themeStyle.spacingTopMedium,
                    themeStyle.spacingBottom,
                    themeStyle.divider,
                    reservationStyles.sectionDivider,
                  ]}
                />
              </View>
              <ReadMore
                category={textCategory.BODY}
                contentContainerStyle={themeStyle.spacingBottom}
                maxChar={300}
                status="info"
                value={eventInfo.description}
              />
              {/* Dress Code */}
              {Boolean(eventInfo.dress_code) && (
                <Fragment>
                  <ResponsiveText
                    category={textCategory.HEADING}
                    style={[themeStyle.bold, themeStyle.spacingBottomMedium]}
                  >
                    Dress Code
                  </ResponsiveText>
                  <ListItem
                    contentContainerStyle={themeStyle.spacingBottom}
                    leftIcon={{ name: 'checkmark' }}
                    title={
                      <ResponsiveText category={textCategory.BODY}>
                        {eventInfo.dress_code}
                      </ResponsiveText>
                    }
                  />
                </Fragment>
              )}
              {/* Cancellation Policy */}
              <ReservationCancellationPolicy
                value={eventInfo.cancellation_policy}
              />
              <Divider
                dataSet={{ media: reservationStyleIds.sectionDivider }}
                style={[
                  themeStyle.sectionSeparator,
                  reservationStyles.sectionDivider,
                ]}
              />
              <MapsAndInfo
                latitude={Number(latitude)}
                location_address={eventInfo.location_address}
                longitude={Number(longitude)}
                name={eventInfo.store?.name}
                storeId={eventInfo?.store?.id}
              />
            </View>
            {!constants.isWeb && <View style={themeStyle.pageBottomSpacer} />}
          </View>
        }
        RightComponent={<OverviewAndPartySize {...overviewAndPartySizeProps} />}
        backgroundColor={null} // to remove safearea on the top
        rightComponentWidth={285}
        withFooter={!isPreview}
        stickyMobile
      />
      {/* Checking availability loader modal */}
      <CheckAvailability ref={availabilityRef} />
    </Fragment>
  );
};

export default ReservationEventDetails;
